<template>
  <!-- The overview: the address on top, the bar with its meters, then three columns: what hangs on the address
       (tenant, hardware), the selected meter (and its breaker), and what its vendor knows of it -->
  <div class="tw-bg-white tw-rounded-xl tw-shadow-sm tw-p-4 tw-flex tw-flex-col tw-gap-3">
    <slot name="header" />

    <!-- the meters at the address; picking one only switches this section. Actions act on the selected meter. -->
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap tw-border-y tw-border-gray-100 tw-py-2">
      <div class="tw-flex-1 tw-min-w-0">
        <slot name="tabs" />
      </div>
      <!-- disabled until the selected meter's own detail is in: what it can do depends on what it is -->
      <action-buttons
        v-if="meterDetail && meterCapabilities.length"
        class="tw-transition-opacity"
        :class="{ 'tw-opacity-50 tw-pointer-events-none': isPreviousData }"
        :capabilities="meterCapabilities"
        :is-pinging="isPinging"
        :is-polling="isPolling"
        :is-fetching-live="isFetchingLive"
        @ping="handlePing"
        @poll="handlePoll"
        :is-c4a="isCast4allMeter"
        :is-running-pv-test="pvTest.running"
        @live-production="handleLiveProduction"
        @pv-production-test="startPvProductionTest"
        @show-commands="showCommandHistory = true"
      />
    </div>

    <!-- PV production test (Cast4all): average over a few minutes, not a live value -->
    <pv-production-test
      v-if="isCast4allMeter && (pvTest.running || pvTest.result || pvTest.error)"
      :running="pvTest.running"
      :result="pvTest.result"
      :error="pvTest.error"
    />
    <!-- Live productie: on-demand uitlezing van de meter zelf -->
    <live-production
      v-if="meterKind === 'polling' && (liveResult.data || liveResult.error)"
      :data="liveResult.data"
      :error="liveResult.error"
    />
    <poll-output v-if="receivedChunks.length > 0" :chunks="receivedChunks" />

    <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4 tw-items-start">
      <!-- Col 1: tenant and hardware, from the address -->
      <div class="tw-min-w-0 tw-flex tw-flex-col tw-gap-2">
        <slot name="address" :hardwareMeter="hardwareMeter" />
      </div>

      <div v-if="!meterDetail" class="tw-min-w-0 lg:tw-col-span-2 tw-flex tw-items-center tw-gap-2 tw-py-4 tw-text-sm tw-text-gray-400">
        <template v-if="isLoading">
          <span class="tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin" />
          Meter laden...
        </template>
        <span v-else class="tw-text-red-500">Meter {{ serialNumber }} niet gevonden of fout opgetreden.</span>
      </div>
      <template v-else>
        <!-- Col 2: the meter, whatever reads it, and its breaker -->
        <div
          class="tw-min-w-0 tw-flex tw-flex-col tw-gap-2 tw-transition-opacity"
          :class="{ 'tw-opacity-50 tw-pointer-events-none': isPreviousData }"
        >
          <meter-card
            :serial-number="serialNumber"
            :meter-detail="meterDetail"
            :is-loading="isLoading || isPreviousData"
            :device="device"
            :address-kind="addressKind"
          />
          <!-- Only for meters with a breaker -->
          <breaker-card
            v-if="hasBreaker"
            :breaker-status="breakerStatus"
            :unavailable-reason="breakerUnavailableReason"
            :unknown-reason="meterKind === 'c4a' ? 'Breakerstatus onbekend bij Cast4all: nog nooit geschakeld of getest' : undefined"
            :note="breakerNote?.text ?? null"
            :note-is-error="!!breakerNote?.isError"
            :is-toggling="isTogglingBreaker || isSwitchingC4aBreaker"
            @toggle="handleToggleBreaker"
          />
        </div>

        <!-- Col 3: what the vendor knows: the polling server (DLMS, SIM), Cast4all or SolarEdge -->
        <div
          class="tw-min-w-0 tw-flex tw-flex-col tw-gap-2 tw-transition-opacity"
          :class="{ 'tw-opacity-50 tw-pointer-events-none': isPreviousData }"
        >
          <polling-server-status
            v-if="meterKind === 'polling'"
            :meter-detail="meterDetail"
            :received-chunks="receivedChunks"
          >
            <template #actions>
              <button :class="BUTTON_CLASS" title="Live status bij 1NCE en de SIM-gegevens uit de database" @click="showSimStatus = true">SIM</button>
            </template>
          </polling-server-status>
          <!-- Cast4all and SolarEdge: a summary here, the full panel in a modal -->
          <c4a-summary v-else-if="meterKind === 'c4a'" :serial-number="serialNumber">
            <template #actions>
              <div class="tw-flex tw-gap-1.5">
                <button :class="BUTTON_CLASS" title="Provider, ICCID en netwerkstatus" @click="showSimStatus = true">SIM</button>
                <!-- a meter Cast4all reads through the inverter API is at SolarEdge too -->
                <button
                  v-if="c4aStatus?.meter?.api_meter"
                  :class="BUTTON_CLASS"
                  title="Site, omvormers en meldingen bij SolarEdge"
                  @click="showSolarEdge = true"
                >SolarEdge</button>
                <button :class="BUTTON_CLASS" title="Meter en communicator live bij Cast4all" @click="showC4a = true">Details</button>
              </div>
            </template>
          </c4a-summary>
          <solar-edge-summary v-else :serial-number="serialNumber">
            <template #actions>
              <button :class="BUTTON_CLASS" title="Site, omvormers en meldingen, ook live bij SolarEdge" @click="showSolarEdge = true">Details</button>
            </template>
          </solar-edge-summary>
        </div>
      </template>
    </div>

    <details-modal :visible="showSimStatus" title="SIM-status" @close="showSimStatus = false">
      <sim-status :serial-number="serialNumber" />
    </details-modal>
    <details-modal :visible="showC4a" title="Cast4all" @close="showC4a = false">
      <c4a-status :serial-number="serialNumber" />
    </details-modal>
    <details-modal :visible="showSolarEdge" title="SolarEdge" width-class="tw-max-w-4xl" @close="showSolarEdge = false">
      <solar-edge-status :serial-number="serialNumber" />
    </details-modal>

    <!-- Command history modal -->
    <command-history
      :serial-number="serialNumber"
      :is-c4a="isCast4allMeter"
      :visible="showCommandHistory"
      @close="showCommandHistory = false"
    />
  </div>
</template>

<script setup>
// The overview section of the address page, around one meter of the address: what it is (polling server,
// Cast4all, SolarEdge), what can be done with it (ping, poll, live readout, PV test, breaker), its status at
// the polling server, Cast4all or SolarEdge, and the command history. The address parts come in through the
// slots header, tabs and address. Fetches its own meter detail, so switching meters only reloads the meter.
// Results of polls and live readouts are kept per serial while the page is open.
import { ref, computed } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { notifications } from '@/util'
import fetchData from '@/util/fetch'
import store from '@/store'
import MeterCard from './MeterCard.vue'
import ActionButtons from './ActionButtons.vue'
import BreakerCard from './BreakerCard.vue'
import PollOutput from './PollOutput.vue'
import LiveProduction from './LiveProduction.vue'
import PvProductionTest from './PvProductionTest.vue'
import CommandHistory from './CommandHistory.vue'
import PollingServerStatus from './PollingServerStatus.vue'
import C4aStatus from './C4aStatus.vue'
import C4aSummary from './C4aSummary.vue'
import SolarEdgeSummary from './SolarEdgeSummary.vue'
import SolarEdgeStatus from './SolarEdgeStatus.vue'
import SimStatus from './SimStatus.vue'
import DetailsModal from './DetailsModal.vue'
import { usePvProductionTest } from './composables/usePvProductionTest'
import { useMeterDetail } from './composables/useMeterDetail'
import { useC4aStatus } from './composables/useC4aStatus'
import { useC4aBreaker } from './composables/useC4aBreaker'
import { formatDateTimeNL } from './formatters'

const props = defineProps({
  serialNumber: { type: String, required: true },
  // this meter's entry of the address view (useAddressDevices): first and last data, history
  device: { type: Object, default: null },
  // regular | cvz | placeholder
  addressKind: { type: String, default: 'regular' },
})

const queryClient = useQueryClient()
const serialNumber = computed(() => props.serialNumber)
const showCommandHistory = ref(false)
const showSimStatus = ref(false)
const showSolarEdge = ref(false)
const showC4a = ref(false)

const BUTTON_CLASS = 'tw-text-xs tw-font-medium tw-px-2 tw-py-1 tw-rounded tw-border tw-border-gray-300 tw-text-gray-700 hover:tw-bg-white hover:tw-border-blue-400 hover:tw-text-blue-600 tw-transition-colors'

// keepPreviousData: when switching, the previous meter stays visible (dimmed) until the new one is loaded.
// Everything worked out from the detail (kind, capabilities, breaker) is the previous meter's until then, so
// actions wait for isPreviousData to clear and the Cast4all lookups are not started for the wrong meter.
const { data: meterDetail, isLoading, isPreviousData } = useMeterDetail(serialNumber, { keepPreviousData: true })
const isCurrent = computed(() => !!meterDetail.value && !isPreviousData.value)

// After an action the meter's own data and every chart of the address may have changed: the address
// charts sum this meter in under the page serial, so refresh them all (only the active ones refetch)
const refreshAfterAction = (serial) => {
  queryClient.invalidateQueries(['meterDetail', serial])
  queryClient.invalidateQueries(['healthCheck', serial])
  for (const key of ['meterGraph', 'meterGraphSplit', 'multiDay', 'yearAnalysis', 'fiveYear']) {
    queryClient.invalidateQueries([key])
  }
}

// Per-meter polling state
const pollingSerialNumber = ref(null)
const chunksBySerial = ref({})

const isPolling = computed(() => pollingSerialNumber.value === serialNumber.value)
const receivedChunks = computed(() => chunksBySerial.value[serialNumber.value] ?? [])

// Live productie is een on-demand uitlezing: de polling server belt de meter over
// zijn mobiele verbinding, dus alleen bij een klik en nooit gecached. Per meter
// bewaard zodat een resultaat niet meelift naar een andere meter.
const liveFetchingSerial = ref(null)
const liveBySerial = ref({})

const isFetchingLive = computed(() => liveFetchingSerial.value === serialNumber.value)
const liveResult = computed(
  () => liveBySerial.value[serialNumber.value] ?? { data: null, error: null }
)

// Ping
// Meters die alleen Cast4all uitleest (sources ['c4a']) worden via Cast4all gepingd: daar
// draait een verbindingstest die bij een onbereikbare meter pas na ~60 s afloopt.
// Niet op meter_type_id: SolarEdge-meters hebben die ook niet.
const meterSources = computed(() => meterDetail.value?.sources ?? [])
const isCast4allMeter = computed(
  () => meterSources.value.includes('c4a') && !meterSources.value.includes('dlms')
)
// Switched to SolarEdge (from 2026-11-01): no Cast4all, no polling server
const isSolarEdgeMeter = computed(() => meterSources.value.includes('solaredge'))
const meterKind = computed(() => {
  if (isSolarEdgeMeter.value) return 'solaredge'
  return isCast4allMeter.value ? 'c4a' : 'polling'
})
// Polling server: live readout; Cast4all: a production test of a few minutes instead;
// SolarEdge: nothing to send to the meter, its status is in the SolarEdge panel
const METER_CAPABILITIES = {
  polling: ['ping', 'poll', 'live', 'history'],
  c4a: ['ping', 'poll', 'pv-test', 'history'],
  solaredge: [],
}
const meterCapabilities = computed(() => METER_CAPABILITIES[meterKind.value])
// Cast4all live status, for a Cast4all meter: its communicator tells whether it has a breaker
const { data: c4aStatus } = useC4aStatus(serialNumber, computed(() => isCurrent.value && meterKind.value === 'c4a'))
// Which meters have a breaker, as in the tickets portal (MeterActivationView, services/c4a.has_breaker):
// polling: a breaker status in the polling DB, or meter type 3 (KWHIQ_with_breaker) which has one without
// a status yet; Cast4all: a communicator type ending in '+BR' (e.g. 'Kamstrup+BR'); SolarEdge: none.
const BREAKER_METER_TYPE_ID = 3
const hasBreaker = computed(() => {
  if (meterKind.value === 'c4a') return /\+BR$/i.test(c4aStatus.value?.communicator?.type ?? '')
  if (meterKind.value !== 'polling') return false
  const status = meterDetail.value?.breaker_status
  return status === 'On' || status === 'Off' || meterDetail.value?.meter_type_id === BREAKER_METER_TYPE_ID
})

// The meter as hardware, for the hardware table: Cast4all's communicator type (e.g. 'Kamstrup+BR') or the
// polling server's meter type (E350, P1MB, KWHIQ_with_breaker, ...); a SolarEdge site has none
const hardwareMeter = computed(() => {
  if (meterKind.value === 'c4a') {
    const status = c4aStatus.value
    if (!status) return null
    if (status.communicator) return { value: status.communicator.type || 'Onbekend', title: 'Communicator volgens Cast4all' }
    return { value: status.meter?.api_meter ? 'API van de omvormer' : 'Geen communicator', title: 'Volgens Cast4all' }
  }
  if (meterKind.value !== 'polling' || !meterDetail.value?.meter_type_name) return null
  return { value: meterDetail.value.meter_type_name, title: meterDetail.value.meter_type_description || '' }
})

// A Solarminds Cast4all breaker is read and switched through the tickets endpoints of the meter activation
// page (useC4aBreaker); the polling server's breaker only knows DLMS meters
const isSolarmindsC4a = computed(() => meterKind.value === 'c4a' && c4aStatus.value?.account === 'solarminds')
const {
  info: c4aBreaker,
  errorMessage: c4aBreakerError,
  isSwitching: isSwitchingC4aBreaker,
  switchError: c4aSwitchError,
  setBreaker: setC4aBreaker,
} = useC4aBreaker(serialNumber, computed(() => isCurrent.value && isSolarmindsC4a.value && hasBreaker.value))

// On | Off, anything else is unknown
const breakerStatus = computed(() =>
  meterKind.value === 'c4a' ? c4aBreaker.value?.breaker_status ?? null : meterDetail.value?.breaker_status
)
const breakerUnavailableReason = computed(() => {
  if (meterKind.value !== 'c4a') return null
  if (!isSolarmindsC4a.value) return 'Schakelen kan alleen bij meters van het Solarminds-account van Cast4all'
  if (c4aBreakerError.value) return c4aBreakerError.value
  const pending = c4aBreaker.value?.pending
  if (pending) return `Cast4all verwerkt nog een schakeling naar ${pending === 'On' ? 'aan' : 'uit'}`
  return null
})
// Cast4all: where the status comes from, and a failed last switch
const breakerNote = computed(() => {
  if (meterKind.value !== 'c4a') return null
  if (c4aSwitchError.value) return { text: c4aSwitchError.value, isError: true }
  const info = c4aBreaker.value
  if (info?.last_switch?.status === 'Failure') {
    return { text: `Laatste schakeling mislukt: ${info.last_switch.result_message || 'onbekende fout'}`, isError: true }
  }
  if (info?.source && info?.source_time) {
    const from = info.source === 'switch' ? 'de laatste schakeling' : 'de laatste productietest'
    return { text: `Volgens ${from} (${formatDateTimeNL(info.source_time)})`, isError: false }
  }
  return null
})

const { pvTest, startPvProductionTest } = usePvProductionTest(serialNumber)
// per serial, like polling and live: switching tabs during a ping does not carry its spinner along
const pingingSerial = ref(null)
const isPinging = computed(() => pingingSerial.value === serialNumber.value)
const handlePing = async () => {
  if (!isCurrent.value) return
  const pingSerial = serialNumber.value
  pingingSerial.value = pingSerial
  try {
    let timeoutId
    const abortController = new AbortController()
    const pingTimeoutMs = isCast4allMeter.value ? 90000 : 10000
    const timeoutPromise = new Promise((_, reject) =>
      timeoutId = setTimeout(() => { abortController.abort(); reject(new Error('Timeout')) }, pingTimeoutMs)
    )
    const fetchPromise = fetchData(`/eleena/api/v1/meters/${pingSerial}/ping`, {
      signal: abortController.signal,
    })
    const result = await Promise.race([fetchPromise, timeoutPromise])
    clearTimeout(timeoutId)
    const alive = result?.data?.alive
    const c4aReason = result?.data?.connection_test?.result_message
    notifications.addNotification({
      message: alive
        ? `Meter ${pingSerial} bereikbaar ✓`
        : `Meter ${pingSerial} niet bereikbaar${c4aReason ? ` (Cast4all: ${c4aReason})` : ''}`,
      type: alive ? 'success' : 'danger',
    })
    refreshAfterAction(pingSerial)
  } catch (e) {
    notifications.addNotification({ message: `Ping ${pingSerial} mislukt: ${e.message}`, type: 'danger' })
  } finally {
    if (pingingSerial.value === pingSerial) pingingSerial.value = null
  }
}

// Poll
const handlePoll = async () => {
  if (!isCurrent.value) return
  const pollSerial = serialNumber.value
  pollingSerialNumber.value = pollSerial
  chunksBySerial.value = { ...chunksBySerial.value, [pollSerial]: [] }
  try {
    // Geen fetchData: die wacht op de hele body, hier lezen we de stream per chunk
    const response = await fetch(`/eleena/api/v1/meters/${pollSerial}/poll`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${store.getters['auth/getToken']}`,
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      const detail = typeof body.detail === 'string' ? body.detail : null
      throw new Error(body.message || detail || `HTTP ${response.status}`)
    }
    if (!response.body) throw new Error('Geen response body')

    const decoder = new TextDecoder('utf-8')
    const reader = response.body.getReader()
    let buffer = ''

    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const parts = buffer.split('\n')
      buffer = parts.pop()
      for (const part of parts) {
        if (part.trim()) {
          const chunk = JSON.parse(part)
          chunksBySerial.value = {
            ...chunksBySerial.value,
            [pollSerial]: [...(chunksBySerial.value[pollSerial] ?? []), chunk],
          }
        }
      }
    }
    refreshAfterAction(pollSerial)
    notifications.addNotification({ message: `Polling ${pollSerial} voltooid`, type: 'success' })
  } catch (e) {
    notifications.addNotification({ message: `Polling mislukt: ${e.message}`, type: 'danger' })
  } finally {
    pollingSerialNumber.value = null
  }
}

// Live productie uitlezen
const handleLiveProduction = async () => {
  if (!isCurrent.value) return
  const liveSerial = serialNumber.value
  if (liveFetchingSerial.value === liveSerial) return
  liveFetchingSerial.value = liveSerial
  liveBySerial.value = { ...liveBySerial.value, [liveSerial]: { data: null, error: null } }
  try {
    const result = await fetchData(`/eleena/api/v1/meters/${liveSerial}/live-production`)
    liveBySerial.value = {
      ...liveBySerial.value,
      [liveSerial]: { data: result?.data ?? null, error: null },
    }
  } catch (e) {
    // fetchData gooit bij een foutstatus de geparseerde JSON body, die de melding
    // van de API bevat, en anders een gewone Error.
    liveBySerial.value = {
      ...liveBySerial.value,
      [liveSerial]: {
        data: null,
        error: e?.message || 'Onbekende fout bij het uitlezen van de meter',
      },
    }
  } finally {
    if (liveFetchingSerial.value === liveSerial) liveFetchingSerial.value = null
  }
}

// Breaker toggle
const togglingSerial = ref(null)
const isTogglingBreaker = computed(() => togglingSerial.value === serialNumber.value)
const handleToggleBreaker = async () => {
  if (!isCurrent.value) return
  if (meterKind.value === 'c4a') {
    // On: the panels deliver, so switch off; Off: switch on
    const on = breakerStatus.value !== 'On'
    await setC4aBreaker(on)
    if (!c4aSwitchError.value) {
      notifications.addNotification({
        message: `Cast4all schakelt de breaker ${on ? 'aan' : 'uit'}; dat kan even duren`,
        type: 'success',
      })
    }
    return
  }
  // When PV output is "On" (actief), the breaker is NOT engaged → call /activate-breaker to disconnect
  // When PV output is "Off" (inactief), the breaker IS engaged → call /deactivate-breaker to reconnect
  const action = meterDetail.value.breaker_status === 'On' ? 'activate' : 'deactivate'
  const breakerSerial = serialNumber.value
  togglingSerial.value = breakerSerial
  try {
    await fetchData(`/eleena/api/v1/meters/${breakerSerial}/${action}-breaker`, { method: 'POST' })
    queryClient.invalidateQueries(['meterDetail', breakerSerial])
    notifications.addNotification({ message: `Breaker ${action === 'activate' ? 'geactiveerd' : 'gedeactiveerd'}`, type: 'success' })
  } catch (e) {
    notifications.addNotification({ message: `Fout bij breaker: ${e.message}`, type: 'danger' })
  } finally {
    if (togglingSerial.value === breakerSerial) togglingSerial.value = null
  }
}
</script>
